import { useEffect, useState } from "react";
import { CheckCircle2, ZoomIn, ZoomOut, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, pub, errMsg } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { LanguageBar, langLabel, makeT } from "@/lib/i18n";
import { dateFormat, setDateFormat } from "@/lib/status";
import { LargePrintToggle } from "@/lib/largePrint";
import { missingFields } from "@/lib/fieldRules";
import { ResetButton, ResetDialog } from "@/components/ResetDialog";

const BIG = "h-14 px-8 text-lg rounded-xl";

function SigValue({ v }) {
  if (!v) return null;
  if (v.startsWith("data:image")) return <img src={v} alt="Your signature" className="h-full w-full object-contain" />;
  return <span className="text-blue-900 italic" style={{ fontFamily: "Georgia, serif" }}>{v.replace("typed:", "")}</span>;
}

const INPUT_TYPE = { number: "text", email: "email", phone: "tel", date: "text" };
const INPUT_MODE = { number: "decimal", phone: "tel", email: "email" };

function FieldInput({ f, v, style, setVal, testid }) {
  const base = "absolute p-1 text-sm bg-yellow-50 border border-yellow-400 rounded";
  if (f.type === "textarea") return <textarea style={style} maxLength={2000} placeholder={f.label} className={`${base} resize-none leading-tight`} aria-label={f.label} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} data-testid={testid} />;
  if (f.type === "choice") return (
    <select style={style} className={base} aria-label={f.label} value={v || ""} onChange={(e) => setVal(f.id, e.target.value || null)} data-testid={testid}>
      <option value="">Select...</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>);
  return <Input style={style} type={INPUT_TYPE[f.type] || "text"} inputMode={INPUT_MODE[f.type]} autoComplete={f.type === "name" ? "name" : "off"} className={`${base} h-auto`} aria-label={f.label} placeholder={f.type === "date" ? dateFormat() : f.label || ""} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} data-testid={testid} />;
}

function DocView({ token, doc, values, setVal, zoom, t }) {
  const [pad, setPad] = useState(null);
  const [tmp, setTmp] = useState(null);
  return (
    <div className="space-y-6">
      {doc.pages.map((pg, n) => (
        <div key={n} className="relative mx-auto bg-white shadow-md" style={{ width: `${zoom}%` }} data-testid={`sign-page-${n}`}>
          <img src={`${API}/sign/${token}/doc/${doc.doc_id}/page/${n}?t=${doc.template_id || ""}`} alt={`${doc.name} page ${n + 1}`} className="w-full block" />
          {doc.fields.filter((f) => f.page === n).map((f) => {
            const v = values[f.id];
            const style = { left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` };
            if (f.prefill) return <div key={f.id} style={style} className="absolute flex items-end text-[1.6vw] md:text-sm px-1 bg-sky-50/70">{v}</div>;
            if (f.type === "checkbox") return (
              <button key={f.id} style={{ ...style, minWidth: 28, minHeight: 28 }} onClick={() => setVal(f.id, !v)} aria-label={f.label} aria-pressed={!!v}
                className={`absolute border-2 rounded grid place-items-center ${v ? "bg-[#0B3D8C] border-[#082F6B] text-white" : "bg-yellow-100 border-yellow-500 animate-pulse"}`} data-testid={`field-${doc.doc_id}-${f.id}`}>{v ? "X" : ""}</button>
            );
            if (f.type === "signature" || f.type === "initials") return (
              <button key={f.id} style={style} onClick={() => { setTmp(null); setPad(f); }} aria-label={f.label}
                className={`absolute border-2 rounded flex items-center justify-center text-xs ${v ? "border-green-600 bg-white" : "border-yellow-500 bg-yellow-100"}`} data-testid={`field-${doc.doc_id}-${f.id}`}>
                {v ? <SigValue v={v} /> : <span className="flex items-center gap-1 font-semibold"><PenLine className="w-4 h-4" />{t(f.type === "initials" ? "Tap to initial" : "Tap to sign")}{f.label ? ` - ${f.label}` : ""}</span>}
              </button>
            );
            return <FieldInput key={f.id} f={f} v={v} style={style} setVal={setVal} testid={`field-${doc.doc_id}-${f.id}`} />;
          })}
        </div>
      ))}
      <Dialog open={!!pad} onOpenChange={(o) => !o && setPad(null)}>
        <DialogContent className="max-w-xl font-kiosk" data-testid="signature-dialog">
          <DialogHeader><DialogTitle className="text-2xl">{pad?.label}</DialogTitle></DialogHeader>
          {pad && <SignaturePad kind={pad.type} onChange={setTmp} testid="patient-pad" t={t} />}
          <Button className={`${BIG} bg-[#0B3D8C] hover:bg-[#082F6B]`} disabled={!tmp} onClick={() => { setVal(pad.id, tmp); setPad(null); }} data-testid="signature-apply-btn">{t("Apply")}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SigningFlow({ token, data, onFinished, lang, setLang, lp, onReload }) {
  const t = makeT(lang);
  setDateFormat(data.date_format);
  const init = Object.fromEntries(data.documents.map((d) => [d.doc_id, Object.fromEntries(d.fields.map((f) => [f.id, f.value ?? (f.type === "checkbox" ? false : null)]))]));
  const [step, setStep] = useState(data.erecord ? 1 : 0);
  const [resume, setResume] = useState(1);
  const [choice, setChoice] = useState("");
  const [values, setValues] = useState(init);
  const [zoom, setZoom] = useState(100);
  const [intent, setIntent] = useState(false);
  const [poaSig, setPoaSig] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [decline, setDecline] = useState(false);
  const [key] = useState(() => Math.random().toString(36).slice(2));
  const [resetOpen, setResetOpen] = useState(false);
  const [padKey, setPadKey] = useState(0);
  const [syncing, setSyncing] = useState(Boolean(lang && data.language && lang !== data.language));
  const [langAlert, setLangAlert] = useState(false);
  const docs = data.documents;
  const di = step - 1;
  const missing = (d) => missingFields(d.fields, values[d.doc_id]);
  const setVal = (doc) => (fid, v) => setValues({ ...values, [doc]: { ...values[doc], [fid]: v } });

  const saveChoice = async () => {
    try { await pub.post(`/sign/${token}/erecord`, { choice, language: lang }); setStep(resume); } catch (e) { setErr(errMsg(e)); }
  };
  const changeLang = async (l) => {
    setLang(l); setSyncing(true);
    try {
      const { data: r } = await pub.post(`/sign/${token}/language`, { language: l });
      if (r.swapped && onReload) { onReload(); return; }
      if (r.erecord_reset) { setResume(step >= 1 ? step : 1); setChoice(""); setStep(0); }
    } catch (e) { setErr(errMsg(e)); }
    setSyncing(false);
  };
  const notAvail = docs.filter((d) => d.language && d.language !== lang);
  const ackKey = `lang-ack:${token}:${lang}:${docs.map((d) => d.template_id).join(",")}`;
  useEffect(() => {
    if (!syncing && notAvail.length && !sessionStorage.getItem(ackKey)) setLangAlert(true);
  }, [ackKey, syncing]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeLangAlert = () => { sessionStorage.setItem(ackKey, "1"); setLangAlert(false); };
  const disc = data.disclosures?.[lang] || data.disclosure;
  useEffect(() => { if (lang && data.language && lang !== data.language) { changeLang(lang); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const anyDrawn = Object.values(values).some((o) => Object.values(o).some((v) => typeof v === "string" && v.startsWith("data:image")));
      const { data: r } = await pub.post(`/sign/${token}/submit`, { values, intent, signature_method: anyDrawn ? "drawn" : "typed", idempotency_key: key, poa_signature: poaSig || undefined });
      onFinished(r);
    } catch (e) { setErr(`${errMsg(e)} - ${t("retry_note")}`); } finally { setBusy(false); }
  };
  const doReset = async (scope) => {
    const onReview = step === docs.length + 1;
    const target = scope === "document" && !onReview ? [docs[di].doc_id] : docs.map((d) => d.doc_id);
    setValues({ ...values, ...Object.fromEntries(target.map((id) => [id, init[id]])) });
    if (scope === "packet") { setIntent(false); setPoaSig(null); setPadKey(padKey + 1); setStep(1); }
    setResetOpen(false); setErr("");
    try { await pub.post(`/sign/${token}/reset`, { scope, doc_ids: target }); } catch (e) { /* audit only */ }
  };
  const doDecline = async (outcome) => {
    try { const { data: r } = await pub.post(`/sign/${token}/decline`, { outcome }); onFinished(r); } catch (e) { setErr(errMsg(e)); }
  };

  return (
    <div className="min-h-screen font-kiosk text-slate-800 ace-watermark" style={{ background: "#FAF8F5" }} data-testid="signing-flow">
      <header className="relative bg-white border-b px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3" data-testid="signing-header">
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: "linear-gradient(90deg,#4F46E5,#059669,#0284C7,#D97706,#C026D3,#E11D48)" }} />
        <img src="/ace-mark.png" alt="ACE eSIGN" title="ACE eSIGN" className="h-10 w-auto shrink-0" data-testid="signing-brand-mark" />
        <div className="flex-1 min-w-[160px]"><p className="font-semibold text-lg leading-tight">{data.legal_entity}</p><p className="text-sm text-slate-500">{t("Signing for")} {data.patient_name}</p></div>
        <LargePrintToggle lp={lp} label={t("Large text")} />
        <button className="text-sm underline text-slate-500 h-12 px-3" onClick={() => setDecline(true)} data-testid="signer-decline-link">{t("I don't want to sign now")}</button>
      </header>
      <main className="max-w-4xl mx-auto p-4 sm:p-8">
        {err && <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800" role="alert" data-testid="signing-error">{err}</div>}
        <div className="mb-6"><LanguageBar lang={lang} setLang={changeLang} t={t} compact={step >= 1} /></div>
        {step === 0 && (
          <section className="bg-white rounded-2xl border p-6 sm:p-10 fade-in" data-testid="erecord-step">
            <h1 className="text-3xl font-semibold">{t("How would you like your records?")}</h1>
            <p className="mt-4 text-lg leading-relaxed" data-testid="disclosure-text">{disc.text}</p>
            <p className="text-xs text-slate-400 mt-2">{t("Disclosure")} {disc.version}</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {[["electronic", t("Sign and receive electronically")], ["paper_copy_requested", t("Sign here, and I also want a paper copy")]].map(([v, l]) => (
                <button key={v} onClick={() => setChoice(v)} aria-pressed={choice === v} data-testid={`erecord-${v}`}
                  className={`p-6 rounded-xl border-2 text-left text-lg transition-colors ${choice === v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
            <Button className={`${BIG} mt-8 bg-[#0B3D8C] hover:bg-[#082F6B]`} disabled={!choice} onClick={saveChoice} data-testid="erecord-continue-btn">{t("Continue")}</Button>
          </section>
        )}
        {step >= 1 && step <= docs.length && (
          <section className="fade-in" key={di}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div><p className="text-sm text-slate-500">{t("Document")} {di + 1} {t("of")} {docs.length}</p><h1 className="text-2xl sm:text-3xl font-semibold">{docs[di].name}</h1></div>
              <div className="flex gap-2">
                <ResetButton onClick={() => setResetOpen(true)} t={t} />
                <Button variant="outline" className="h-12 w-12" onClick={() => setZoom(Math.max(60, zoom - 15))} aria-label={t("Zoom out")} data-testid="zoom-out-btn"><ZoomOut /></Button>
                <Button variant="outline" className="h-12 w-12" onClick={() => setZoom(Math.min(160, zoom + 15))} aria-label={t("Zoom in")} data-testid="zoom-in-btn"><ZoomIn /></Button>
              </div>
            </div>
            {docs[di].language && docs[di].language !== lang && <p className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-lg" role="status" data-testid="form-language-unavailable">{t("form_unavailable", { lang: langLabel(lang), form: docs[di].name, shown: langLabel(docs[di].language) })}</p>}
            <div className="overflow-x-auto pb-4"><DocView token={token} doc={docs[di]} values={values[docs[di].doc_id]} setVal={setVal(docs[di].doc_id)} zoom={zoom} t={t} /></div>
            {missing(docs[di]).length > 0 && <p className="mt-4 text-amber-800 text-lg" data-testid="missing-fields">{t("Still needed")}: {missing(docs[di]).join(", ")}</p>}
            <div className="flex gap-3 mt-6">
              {step > 1 && <Button variant="outline" className={BIG} onClick={() => setStep(step - 1)} data-testid="doc-back-btn">{t("Back")}</Button>}
              <Button className={`${BIG} bg-[#0B3D8C] hover:bg-[#082F6B]`} disabled={missing(docs[di]).length > 0} onClick={() => setStep(step + 1)} data-testid="doc-next-btn">{step === docs.length ? t("Review") : t("Next document")}</Button>
            </div>
          </section>
        )}
        {step === docs.length + 1 && (
          <section className="bg-white rounded-2xl border p-6 sm:p-10 fade-in" data-testid="review-step">
            <div className="flex items-center justify-between gap-2 flex-wrap"><h1 className="text-3xl font-semibold">{t("Final review")}</h1><ResetButton onClick={() => setResetOpen(true)} t={t} /></div>
            <ul className="mt-6 space-y-3">{docs.map((d, i) => (
              <li key={d.doc_id} className="flex items-center gap-3 text-lg"><CheckCircle2 className="text-green-600" />{d.name} (v{d.version}){d.language && d.language !== lang && <span className="text-sm px-2 py-0.5 rounded bg-amber-100 text-amber-900" data-testid={`review-lang-only-${i}`}>{t("lang_only", { shown: langLabel(d.language) })}</span>}
                <button className="ml-auto underline text-base text-blue-700 h-12" onClick={() => setStep(i + 1)} data-testid={`review-edit-${i}`}>{t("Review / change")}</button></li>))}
            </ul>
            {data.poa && (
              <div className="mt-8 p-5 rounded-xl border-2 border-slate-200" data-testid="poa-cosign-section">
                <p className="text-lg font-semibold">{t("poa_title")}: {data.poa.name} <span className="font-normal text-slate-500">({data.poa.relationship})</span></p>
                <p className="text-sm text-slate-500 mb-3">{t("poa_optional")}</p>
                <SignaturePad key={padKey} onChange={setPoaSig} testid="poa-pad" t={t} />
              </div>
            )}
            <label className="flex items-start gap-4 mt-8 p-5 rounded-xl border-2 border-slate-200 cursor-pointer text-lg">
              <Checkbox className="w-7 h-7 mt-0.5" checked={intent} onCheckedChange={(v) => setIntent(v === true)} data-testid="intent-checkbox" />
              <span>{t("intent_1")} <b>{data.signer.name}</b>{data.signer.type === "representative" ? ` ${t("intent_rep", { rel: data.signer.relationship, patient: data.patient_name })}` : ""}, {t("intent_2")} {data.legal_entity}.</span>
            </label>
            <Button className={`${BIG} mt-8 w-full sm:w-auto bg-[#0B3D8C] hover:bg-[#082F6B]`} disabled={!intent || busy} onClick={submit} data-testid="submit-signature-btn">{busy ? t("Submitting...") : t("Sign and submit")}</Button>
          </section>
        )}
      </main>
      <ResetDialog open={resetOpen} setOpen={setResetOpen} t={t} multi={docs.length > 1 || step === docs.length + 1}
        docName={step >= 1 && step <= docs.length ? docs[di].name : null} onReset={doReset} />
      <Dialog open={langAlert} onOpenChange={(o) => !o && closeLangAlert()}>
        <DialogContent className="font-kiosk max-w-lg w-[calc(100%-2rem)] rounded-2xl" data-testid="lang-unavailable-dialog">
          <DialogHeader><DialogTitle className="text-2xl">{t("lang_missing_title", { lang: langLabel(lang) })}</DialogTitle></DialogHeader>
          <p className="text-lg">{t("lang_missing_body", { lang: langLabel(lang) })}</p>
          <ul className="list-disc pl-6 text-lg space-y-1" data-testid="lang-unavailable-list">{notAvail.map((d) => <li key={d.doc_id}>{d.name} - <b>{langLabel(d.language)}</b></li>)}</ul>
          <p className="text-base text-slate-600">{t("lang_missing_help")}</p>
          <Button className="h-14 text-lg rounded-xl bg-[#0B3D8C] hover:bg-[#082F6B]" onClick={closeLangAlert} data-testid="lang-unavailable-ok">{t("OK, continue")}</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={decline} onOpenChange={setDecline}>
        <DialogContent className="font-kiosk" data-testid="decline-dialog">
          <DialogHeader><DialogTitle className="text-2xl">{t("Not signing right now?")}</DialogTitle></DialogHeader>
          <p className="text-lg">{t("decline_body")}</p>
          <div className="grid gap-3">
            <Button variant="outline" className={BIG} onClick={() => doDecline("deferred")} data-testid="decline-deferred">{t("I need more time / have questions")}</Button>
            <Button variant="outline" className={BIG} onClick={() => doDecline("unable_to_sign")} data-testid="decline-unable">{t("I'm unable to sign")}</Button>
            <Button variant="outline" className={`${BIG} text-red-700`} onClick={() => doDecline("refused")} data-testid="decline-refused">{t("I do not agree to sign")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
